import React from "react";
import {
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { MonthlyBucket, CashflowAllocation } from "models/CashflowModel";
import { CashflowViewMode } from "../types";
import { formatCurrency, formatMonthLabel } from "../utils";

interface MonthlyComparisonTableProps {
  months: Record<string, MonthlyBucket>;
  monthKeys: string[];
  viewMode: CashflowViewMode;
  onMonthClick: (monthKey: string) => void;
  onPOClick?: (poNumber: string) => void;
}

export default function MonthlyComparisonTable({
  months,
  monthKeys,
  viewMode,
  onMonthClick,
  onPOClick,
}: MonthlyComparisonTableProps) {
  // Compute rows based on view mode
  const rows = React.useMemo(() => {
    if (viewMode === "total") {
      return [{
        label: "Total Outflow",
        values: monthKeys.map(key => months[key]?.total || 0),
      }];
    }

    if (viewMode === "bySupplier") {
      const supplierSet = new Set<string>();
      for (const key of monthKeys) {
        const month = months[key];
        if (month) {
          for (const s of Object.keys(month.bySupplier)) {
            supplierSet.add(s);
          }
        }
      }
      const suppliers = Array.from(supplierSet).sort();

      return suppliers.map(supplier => ({
        label: supplier,
        values: monthKeys.map(key => months[key]?.bySupplier[supplier] || 0),
      }));
    }

    // byPO
    const poSet = new Map<string, { status: string }>();
    for (const key of monthKeys) {
      const month = months[key];
      if (month) {
        for (const entry of month.entries) {
          if (!poSet.has(entry.poNumber)) {
            poSet.set(entry.poNumber, { status: entry.status });
          }
        }
      }
    }

    return Array.from(poSet.entries()).map(([poNumber, meta]) => ({
      label: poNumber,
      status: meta.status,
      values: monthKeys.map(key => {
        const month = months[key];
        if (!month) return 0;
        return month.entries
          .filter((e: CashflowAllocation) => e.poNumber === poNumber)
          .reduce((sum: number, e: CashflowAllocation) => sum + e.trancheAmount, 0);
      }),
    }));
  }, [months, monthKeys, viewMode]);

  // Footer totals
  const totals = monthKeys.map(key => months[key]?.total || 0);
  const grandTotal = totals.reduce((sum, t) => sum + t, 0);

  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  backgroundColor: "background.paper",
                  minWidth: 160,
                }}
              >
                {viewMode === "total" ? "" : viewMode === "bySupplier" ? "Supplier" : "PO Number"}
              </TableCell>
              {monthKeys.map(key => (
                <TableCell
                  key={key}
                  align="right"
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                    minWidth: 120,
                  }}
                  onClick={() => onMonthClick(key)}
                >
                  {formatMonthLabel(key)}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ minWidth: 120, fontWeight: 600 }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const rowTotal = row.values.reduce((sum, v) => sum + v, 0);
              const isPlaced = (row as any).status === "placed";
              const isForecasted = (row as any).status === "forecasted";

              return (
                <TableRow
                  key={idx}
                  sx={
                    viewMode === "byPO"
                      ? {
                          backgroundColor: isPlaced
                            ? "rgba(33, 150, 243, 0.05)"
                            : isForecasted
                            ? "rgba(158, 158, 158, 0.08)"
                            : undefined,
                        }
                      : undefined
                  }
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "background.paper",
                      fontWeight: 500,
                    }}
                  >
                    {viewMode === "byPO" && onPOClick ? (
                      <>
                        <Link
                          component="button"
                          variant="body2"
                          sx={{ fontWeight: 500, cursor: "pointer" }}
                          onClick={() => onPOClick(row.label)}
                        >
                          {row.label}
                        </Link>
                        {isPlaced && (
                          <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                            (Placed)
                          </Typography>
                        )}
                        {isForecasted && (
                          <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                            (Forecasted)
                          </Typography>
                        )}
                      </>
                    ) : (
                      row.label
                    )}
                  </TableCell>
                  {row.values.map((val, mi) => (
                    <TableCell key={mi} align="right">
                      {val > 0 ? formatCurrency(val) : "-"}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {rowTotal > 0 ? formatCurrency(rowTotal) : "-"}
                  </TableCell>
                </TableRow>
              );
            })}

          </TableBody>

          {/* Sticky footer totals row */}
          {rows.length > 1 && (
            <TableFooter>
              <TableRow
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                  backgroundColor: "background.paper",
                  boxShadow: "0 -1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    backgroundColor: "background.paper",
                    fontWeight: 600,
                  }}
                >
                  Total
                </TableCell>
                {totals.map((t, mi) => (
                  <TableCell key={mi} align="right" sx={{ fontWeight: 600 }}>
                    {t > 0 ? formatCurrency(t) : "-"}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {grandTotal > 0 ? formatCurrency(grandTotal) : "-"}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}
